import { Link } from "react-router-dom";
import { ArrowRight, Music4, Radio, Sparkles } from "lucide-react";

/**
 * Shared collection card used on:
 *   • Homepage (HomeCollections)
 *   • /music landing (MusicHub)
 *
 * Variants:
 *   - "premium" → red-brand title, gold sweep-shine, red-brand accents
 *   - "free"    → white title, silver sweep-shine, neutral accents
 *   - "edits"   → red-brand title with Sparkles icon, reuses premium sheen
 */
const VARIANTS = {
  premium: {
    icon: Music4,
    className: "collection-card collection-card--premium",
    kickerClass: "text-red-brand",
    iconClass: "text-red-brand",
    titleClass: "text-red-brand",
    ctaClass:
      "border-red-brand/50 text-red-brand group-hover:bg-red-brand/10",
  },
  free: {
    icon: Radio,
    className: "collection-card collection-card--free",
    kickerClass: "text-white/70",
    iconClass: "text-white",
    titleClass: "text-white",
    ctaClass: "border-white/30 text-white group-hover:bg-white/5",
  },
  edits: {
    icon: Sparkles,
    className: "collection-card collection-card--premium",
    kickerClass: "text-red-brand",
    iconClass: "text-red-brand",
    titleClass: "text-red-brand",
    ctaClass:
      "border-red-brand/50 text-red-brand group-hover:bg-red-brand/10",
  },
};

export default function CollectionCard({
  variant,
  to,
  kicker,
  title,
  lead,
  cta,
  testid,
  children,
}) {
  const v = VARIANTS[variant] || VARIANTS.free;
  const Icon = v.icon;
  return (
    <Link
      to={to}
      data-testid={testid}
      data-variant={variant}
      className={`group block ${v.className} px-7 py-8 sm:px-10 sm:py-12`}
    >
      {/* Animated sweep-shine layer (pure CSS; respects reduced motion) */}
      <span className="collection-card__shine" aria-hidden="true" />

      <div className="collection-card__body">
        <div className="flex items-center gap-3 mb-4">
          <Icon size={18} className={v.iconClass} />
          <div
            className={`font-mono text-[11px] tracking-[0.3em] uppercase ${v.kickerClass}`}
          >
            {kicker}
          </div>
        </div>

        <div
          className={`font-anton text-5xl sm:text-6xl md:text-7xl leading-[0.9] uppercase mb-4 ${v.titleClass}`}
          data-testid={`${testid}-title`}
        >
          {title}
        </div>

        <p className="text-white/70 text-sm sm:text-base leading-relaxed mb-7 max-w-md">
          {lead}
        </p>

        <div
          data-testid={`${testid}-cta`}
          className={[
            "inline-flex items-center gap-2 text-xs font-mono tracking-widest",
            "border rounded-full px-4 py-2 transition-colors",
            v.ctaClass,
          ].join(" ")}
        >
          {cta} <ArrowRight size={12} />
        </div>

        {children}
      </div>
    </Link>
  );
}
